import * as z from 'zod/mini';
import { LIMITS, MAX_WALL_MIN, MINUTES_PER_DAY } from './constants';
import { BlockStatus, Category, DraftSource } from './enums';
import { isValidDate } from '../time/date';

/** Zod schemas of the `data` payload of every record type (data dictionary §3). */

export const personalDateSchema = z
  .string()
  .check(z.refine(isValidDate, { message: 'Expected a valid YYYY-MM-DD date' }));

const wallMinSchema = z.int().check(z.minimum(0), z.maximum(MAX_WALL_MIN));

// A loose object (2026-10c iteration C0, release stage one): fields a newer client writes pass
// through merge, import, export, sync and local edits unchanged. `quickDurationMin` (stage two's
// default duration of the quick-mark tile of the project) is declared for the compile-time type
// only — the boundary takes any number or null without a range check, stage two treats an
// invalid value as null on read (`readQuickDurationMin`), and this build never writes it.
// SCHEMA_VERSION stays 1.
export const projectDataSchema = z.looseObject({
  name: z.string().check(z.minLength(1), z.maxLength(LIMITS.projectNameMax)),
  // A bounded string, not an enum of the selectable palette: a colour name written by another
  // (possibly newer) client must survive sync and import. Unknown names fall back on display.
  color: z.string().check(z.minLength(1), z.maxLength(LIMITS.colorMax)),
  category: z.enum(Category),
  active: z.boolean(),
  sort: z.number(),
  /** Default quick-mark duration in minutes (data dictionary §3); validated on read, not here. */
  quickDurationMin: z.optional(z.nullable(z.number())),
  /** Revision history (data dictionary §3.1); not checked at the boundary, read by `readRevisions`. */
  revisions: z.optional(z.unknown()),
  /** Parent project (data dictionary §3, project hierarchy); not checked at the boundary, normalised on read. */
  parentId: z.optional(z.nullable(z.string())),
  /** Child project only: colour follows the parent (data dictionary §3); `color` still holds a valid name. */
  inheritColor: z.optional(z.nullable(z.boolean())),
});

// A loose object: fields that this build does not know (written by a newer client) pass through
// sync, merge and import unchanged instead of being stripped, so they are never lost (§2.1.9 of
// the 2026-10 redesign). `countRank` stays optional and SCHEMA_VERSION stays 1 for the same
// reason: older records remain valid and older clients keep accepting newer records.
export const timeBlockDataSchema = z
  .looseObject({
    personalDate: personalDateSchema,
    startMin: wallMinSchema,
    endMin: wallMinSchema,
    tz: z.string().check(z.minLength(1), z.maxLength(LIMITS.tzMax)),
    projectId: z.string().check(z.minLength(1)),
    status: z.enum(BlockStatus),
    note: z.nullable(z.string().check(z.maxLength(LIMITS.blockNoteMax))),
    /**
     * Block title (2026-10b §5.1): 1-40 characters after trimming; absent or null means "no
     * title". Older clients pass the field through without displaying it; `title` and `note`
     * are two independent fields.
     */
    title: z.optional(z.nullable(z.string().check(z.minLength(1), z.maxLength(LIMITS.blockTitleMax)))),
    draftSource: z.nullable(z.enum(DraftSource)),
    /** Counting priority of overlapping blocks (data dictionary §3); absent means 0. */
    countRank: z.optional(z.int().check(z.minimum(0))),
  })
  .check(
    z.refine((block) => block.startMin < block.endMin, {
      message: 'startMin must be before endMin',
      path: ['endMin'],
    }),
    z.refine((block) => block.endMin - block.startMin <= MINUTES_PER_DAY, {
      message: 'A block cannot be longer than 24 hours',
      path: ['endMin'],
    }),
    z.refine((block) => (block.status === BlockStatus.Draft) === (block.draftSource !== null), {
      message: 'draftSource is set if and only if the block is a draft',
      path: ['draftSource'],
    }),
  );

// A loose object for the same reason as timeBlockDataSchema (2026-10b iteration §2.2, release
// stage one): fields a newer client writes pass through merge, import, export and local edits
// unchanged. `plannedMin` (stage two's planned time of day) is declared for the compile-time
// type only — the boundary takes any number or null without a range check, stage two treats an
// invalid value as null on read, and this build never writes it. SCHEMA_VERSION stays 1.
export const checkinItemDataSchema = z.looseObject({
  name: z.string().check(z.minLength(1), z.maxLength(LIMITS.checkinNameMax)),
  active: z.boolean(),
  sort: z.number(),
  /** Planned time of day in wall-clock minutes (data dictionary §3); range-checked on read, not here. */
  plannedMin: z.optional(z.nullable(z.number())),
  /**
   * Closed planned-time versions (data dictionary §3). Not checked at the boundary: an invalid
   * value is ignored on read (`readPlanHistory`), never rejected.
   */
  planHistory: z.optional(z.unknown()),
  /**
   * Recurrence rule (data dictionary §3). Type-only at the boundary, like plannedMin: unknown
   * values are interpreted on read (`readCheckinRecurrence`), never rejected. Absent/null = daily.
   */
  repeat: z.optional(z.nullable(z.string())),
  weekdays: z.optional(z.nullable(z.array(z.number()))),
  intervalDays: z.optional(z.nullable(z.number())),
  startDate: z.optional(z.nullable(z.string())),
  endDate: z.optional(z.nullable(z.string())),
  /** Personal date from which a disabled item no longer occurs (data dictionary §3); read leniently. */
  disabledFrom: z.optional(z.nullable(z.string())),
  /** Plan versions (data dictionary §3.2); not checked at the boundary, read by `readCheckinSchedule`. */
  schedule: z.optional(z.unknown()),
  /** Local date-time from which a disabled item no longer occurs (data dictionary §3.2); read leniently. */
  disabledAt: z.optional(z.nullable(z.string())),
  /** Remind at the planned time when the reminder scope is `selected` (data dictionary §3); only `true` counts. */
  remind: z.optional(z.nullable(z.boolean())),
  /** Pinned to the top of the settings list (data dictionary §3); only `true` counts. */
  starred: z.optional(z.nullable(z.boolean())),
  /** Revision history (data dictionary §3.1); not checked at the boundary, read by `readRevisions`. */
  revisions: z.optional(z.unknown()),
});

// A loose object from 2026-10-11 (stage one of a two-stage release, data dictionary §3): fields a
// newer client writes (status, a planned-time snapshot, tz, slot) pass through merge, import,
// export and local ticks unchanged. `doneAt` is the actual completion instant.
export const checkinLogDataSchema = z.looseObject({
  itemId: z.string().check(z.minLength(1)),
  personalDate: personalDateSchema,
  /** Planned local date-time of a timed occurrence (data dictionary §3.2); read leniently. */
  occursAt: z.optional(z.string()),
  /** Date label of an untimed occurrence (data dictionary §3.2); read leniently. */
  date: z.optional(z.string()),
  done: z.boolean(),
  doneAt: z.nullable(z.iso.datetime()),
});

export const dailyNoteDataSchema = z.object({
  personalDate: personalDateSchema,
  text: z.string().check(z.maxLength(LIMITS.dailyNoteMax)),
});

export const templateDataSchema = z
  .object({
    projectId: z.string().check(z.minLength(1)),
    startMin: wallMinSchema,
    endMin: wallMinSchema,
  })
  .check(
    z.refine((template) => template.startMin < template.endMin, {
      message: 'startMin must be before endMin',
      path: ['endMin'],
    }),
    z.refine((template) => template.endMin - template.startMin <= MINUTES_PER_DAY, {
      message: 'A template cannot be longer than 24 hours',
      path: ['endMin'],
    }),
  );

// A loose object (important dates, data dictionary §3, release stage one): fields a newer client
// writes pass through merge, import, export, sync and local edits unchanged. The boundary checks
// structure and generous caps only — `calendar` and `repeat` are bounded strings, not enums, so a
// newer client's calendar or repeat kind is kept; a build that cannot interpret them ignores the
// record on read (`readImportantDate`). The strict limits (60-character title, existing date)
// apply to writes (`importantDateIssues`).
export const importantDateDataSchema = z.looseObject({
  title: z.string().check(z.minLength(1), z.maxLength(LIMITS.importantDateTitleBoundaryMax)),
  date: z.looseObject({
    calendar: z.string().check(z.minLength(1), z.maxLength(LIMITS.importantDateIdBoundaryMax)),
    year: z.number(),
    month: z.number(),
    day: z.number(),
    leap: z.boolean(),
  }),
  repeat: z.string().check(z.minLength(1), z.maxLength(LIMITS.importantDateIdBoundaryMax)),
  leadDays: z.array(z.number()).check(z.maxLength(LIMITS.importantDateLeadDaysBoundaryMax)),
  note: z.optional(z.nullable(z.string().check(z.maxLength(LIMITS.importantDateNoteBoundaryMax)))),
  active: z.boolean(),
});

export type ProjectData = z.infer<typeof projectDataSchema>;
export type TimeBlockData = z.infer<typeof timeBlockDataSchema>;
export type CheckinItemData = z.infer<typeof checkinItemDataSchema>;
export type CheckinLogData = z.infer<typeof checkinLogDataSchema>;
export type DailyNoteData = z.infer<typeof dailyNoteDataSchema>;
export type TemplateData = z.infer<typeof templateDataSchema>;
export type ImportantDateData = z.infer<typeof importantDateDataSchema>;
